/* eslint-disable react/no-array-index-key */
/* eslint-disable no-template-curly-in-string */
import { useContext, useState, useEffect, useRef } from 'react'
import { Form } from '@unform/web'
import { Checkbox, MaskedInput, Select, ButtonNext } from 'components/index'
import { UserContext } from 'context'
import { useFetch, useStorage } from 'hooks'
import * as Yup from 'yup'
import { setLocale } from 'yup'
import { clearString, optionsLevels } from 'helper/utils'
import validate from 'helper/validate.js'
import { Container } from './style'
import Document from '../../Components/Document'

interface IObj {
  [_key: string]: any
}

interface ValidationErrors {
  [key: string]: string
}

const selectOptions = {
  civilState: [
    { label: 'Solteiro', value: 'single' },
    { label: 'Casado', value: 'married' },
    { label: 'Divorciado', value: 'divorced' },
    { label: 'Viúvo', value: 'widower' },
  ],
  specialist: [],
  pixKeyOptions: [
    { label: 'CPF', value: 'CPF' },
    { label: 'CNPJ', value: 'CNPJ' },
    { label: 'E-mail', value: 'EMAIL' },
    { label: 'Telefone', value: 'PHONE' },
    { label: 'Aleatória', value: 'EVP' },
  ],
}

let formData: any = new FormData()

const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/

setLocale({
  mixed: {
    default: 'Não é válido',
  },
  string: {
    min: 'Mínimo de ${min} caracteres',
    max: 'Máximo de ${max} caracteres',
  },
})

const schemaNewPF = (rule: boolean) => {
  const formPixValue = formData.get('pix')
  const parsedPixValue = JSON.parse(formPixValue)
  const pixKeyValue = parsedPixValue?.key
  const pixTypeValue = parsedPixValue?.type

  return Yup.object().shape({
    document: Yup.string()
      .min(14)
      .max(14)
      .test('is-valid-cpf', 'CPF inválido', (value, context) => {
        if (!value || value.trim() === '') return true

        if (!validate.numbers.cpf(value)) {
          return context.createError({ message: 'CPF inválido' })
        }

        return true // CPF válido
      })
      .required('Campo obrigatório'),
    name: Yup.string().required('Campo obrigatório'),
    birthDate: Yup.string().min(10),
    email: Yup.string()
      .matches(emailRegex, 'Email não é válido')
      .required('Campo obrigatório'),
    cellphone: Yup.string().min(15).max(15).required('Campo obrigatório'),
    civilState: Yup.string(),
    address: Yup.object().shape({
      postalCode: Yup.string().min(8),
      address: Yup.string(),
      number: Yup.string(),
      complement: Yup.string(),
      province: Yup.string(),
    }),
    key: Yup.string().test('pix-key-required', function () {
      const { createError } = this // Usar o contexto de Yup
      if (pixTypeValue && !pixKeyValue) {
        return createError({ message: 'Chave PIX é obrigatória' })
      }
      if (
        pixTypeValue === 'CNPJ' &&
        pixKeyValue &&
        !validate.numbers.cnpj(pixKeyValue)
      ) {
        return createError({ message: 'CNPJ inválido' })
      }
      if (
        pixTypeValue === 'CPF' &&
        pixKeyValue &&
        !validate.numbers.cpf(pixKeyValue)
      ) {
        return createError({ message: 'CPF inválido' })
      }
      if (
        pixTypeValue === 'PHONE' &&
        pixKeyValue &&
        !validate.numbers.cellphonePix(pixKeyValue)
      ) {
        return createError({ message: 'Telefone inválido' })
      }
      if (
        pixTypeValue === 'EMAIL' &&
        pixKeyValue &&
        !validate.string.email(pixKeyValue)
      ) {
        return createError({ message: 'Email inválido' })
      }
      return true
    }),
    pixType: Yup.string().test('pix-key-type-required', function () {
      const { createError } = this // Usar o contexto de Yup

      if (!pixTypeValue && pixKeyValue) {
        return createError({
          message: 'Tipo de chave PIX é obrigatória',
        })
      }
      return true
    }),
    customData: Yup.string(),
    pix: Yup.string(),
    gestorId: Yup.string().when([], {
      is: () => !rule,
      then: Yup.string(),
      otherwise: Yup.string(),
    }),
  })
}

export default function PF() {
  const { requestFn, feedback, setPage } = useContext(UserContext)
  const formRef = useRef<HTMLFormElement | any>(null)
  const [personalData, setPersonalData] = useState<any[]>()
  const [addressData, setAddressData] = useState<any[]>()
  const [especialistas] = useStorage('Especialistas')
  const [colaboradores] = useStorage('Colaboradores')
  const [documentList, setDocumentList] = useState<any>([])
  const [user] = useStorage('User')
  const [married, setMarried] = useState<boolean>(false)
  const [includesSpouse, setIncludesSpouse] = useState<boolean>(false)
  const [defaultGestor, setDefaultGestor] = useState({})
  const [pixType, setPixType] = useState('')
  const [pixMask, setPixMask] = useState('')
  const [formIsCreatingUser, setFormIsCreatingUser] = useState(true)
  const inputErrorInitialState = {
    cpf: '',
    email: '',
    cell: '',
  }
  const loadingInputInitialState = {
    cpf: false,
    email: false,
    cell: false,
  }
  const [inputLoading, setInputLoading] = useState(loadingInputInitialState)
  const [inputError, setInputError] = useState(inputErrorInitialState)
  const api = useFetch()

  function formDataToObject(form: any) {
    const object: any = {}
    form.forEach((value: string, key: any) => {
      if (key === 'address') return (object[key] = JSON.parse(value))
      object[key] = value
    })
    return object
  }

  const handleSubmit = async () => {
    if (inputError.cpf || inputError.email || inputError.cell) return
    try {
      const formObject = formDataToObject(formData)
      formRef.current.setErrors({})

      await schemaNewPF(optionsLevels[user.level] === 'SPECIALIST').validate(
        formObject,
        { abortEarly: false }
      )
      feedback.current = { title: 'Conta criada!', error: false }
      requestFn.current = async () => {
        try {
          const doc = clearString(formData.get('document'))
          ;[...formData.entries()].forEach((entry) => {
            const [key, value] = entry
            if (!value || value === '--') return formData.delete(key)
            if (key === 'birthDate') {
              const [d, m, y] = value.split('/')
              formData.set(key, `${y}-${m}-${d}`)
            }
          })

          documentList.forEach((docr: any) => {
            if (!Array.isArray(docr)) return
            formData.append('documentList', docr[1], docr[0])
          })
          const r = await api.fetch(
            'post',
            formData,
            `/gestor/account/${doc}`,
            {
              'Content-type': 'multipart/form-data',
            }
          )

          return r
        } catch (err) {
          console.error(err)
          return err
        }
      }

      setPage((prev) => prev + 1)
    } catch (err) {
      console.error(err)
      const validationErrors: ValidationErrors = {}
      if (err instanceof Yup.ValidationError) {
        err.inner.forEach((error) => {
          if (error.path) {
            validationErrors[error.path] = error.message
          }
        })

        formRef.current.setErrors(validationErrors)
      }
    }
  }

  const updateAddress = () => {
    const curData = formData.get('address')
      ? JSON.parse(formData.get('address'))
      : {}

    Object.entries(curData)?.forEach((entry) => {
      if (!entry) return
      const [key, value] = entry
      const elem = formRef.current.getFieldRef(`address.${key}`)

      elem.current.element.value = value || ''
    })

    if (!curData.postalCode) return

    api
      .fetch('get', null, `/app/get-address/${curData.postalCode}`)
      .then((r: any) => {
        if (r.fail) return

        Object.entries(r.data).forEach(([key, value]: any) => {
          const el = formRef.current.getFieldRef(`address.${key}`)

          if (el) el.current.element.value = value
          curData[key] = value
          formData.set('address', JSON.stringify(curData))
        })
      })
      .catch((err) => {
        console.error('err', err)
      })
  }

  function callFetch() {
    const doc = clearString(formData.get('document'))
    if (doc?.length < 11) return
    api.fetch('get', null, `/gestor/account/${doc}`).then((response) => {
      if (!response.data) return

      const d = response.data.data

      const [pix = {}] = d.account.pixList
      formData.set('address', JSON.stringify(d.address))
      formData.set('pix', JSON.stringify(pix))
      formData.set('cellphone', d.account.cellphone)
      formData.set('email', d.account.email)
      formData.set('gestorId', d.account.gestorId)
      formData.set('motherName', d.user.mothername)
      formData.set('birthDate', d.user.birthDate)
      formData.set('civilState', d.user.civilState)
      formData.set('name', d.user.name)
      formData.set('rg', d.user.rg)

      d.user.rg && formData.set('customData', `{\"rg\":\"${d.user.rg}\"}`)

      const colab = colaboradores.find((item: IObj) => {
        return item.id === d.account.gestorId
      })

      if (pix.type) {
        const selectedOption = selectOptions.pixKeyOptions.find(
          (ih) => ih.value === pix.type
        )
        setPixType(selectedOption ? selectedOption.value : '')
      }

      if (colab) {
        setDefaultGestor({ label: colab.name, value: colab.id })
      }
      // Fill inputs
      ;[...formData.entries()].forEach((entry) => {
        const [key, value] = entry
        if (key === 'address') return
        const elem = formRef.current.getFieldRef(key)
        if (!elem?.element) return
        elem.element.value = value
      })

      setDocumentList(d.account.documentList)
      updateAddress()
    })
  }

  function handleChange(key: string) {
    switch (key) {
      case 'spouse': {
        const curData = formData.get('spouse')
          ? JSON.parse(formData.get('spouse'))
          : {}

        return (e: any) => {
          curData[e.target.name] = e.target.value
          if (!curData[e.target.name]) return delete curData[e.target.name]
          formData.set('spouse', JSON.stringify(curData))
        }
      }
      case 'address': {
        const curData = formData.get('address')
          ? JSON.parse(formData.get('address'))
          : {}

        return (e: any) => {
          const { name, value } = e.target
          const trueName = name.split('.')[1]
          if (trueName === 'postalCode' && value.length === 0)
            return formData.delete('address')
          if (!value || value.length === 0) return delete curData[trueName]
          curData[trueName] = value
          if (!curData[trueName]) return delete curData[trueName]
          formData.set('address', JSON.stringify(curData))
          if (trueName === 'postalCode' && value.length === 9)
            return updateAddress()
        }
      }
      case 'pix': {
        const curData = formData.get('pix')
          ? JSON.parse(formData.get('pix'))
          : {}
        return (e: any) => {
          const { name, value } = e.target

          if (value) {
            curData[name] = value // Adiciona ou atualiza o valor
          } else {
            delete curData[name] // Se o valor estiver vazio, deleta a chave
          }
          if (Object.keys(curData).length) {
            formData.set('pix', JSON.stringify(curData)) // Atualiza o campo 'pix'
          } else {
            formData.delete('pix') // Se o objeto estiver vazio, deleta o campo 'pix'
          }
        }
      }
      case 'rg': {
        return (e: any) => {
          const { value } = e.target
          if (!value) formData.delete('customData')
          formData.set('customData', JSON.stringify({ rg: value }))
        }
      }
      default: {
        return (e: any) => {
          const { value } = e.target
          if (!value) formData.delete(key)
          else formData.set(key, value)
          if (key === 'document') callFetch()
        }
      }
    }
  }

  useEffect(() => {
    const doc = clearString(formData.get('document'))

    if (doc?.length < 11) return

    api.fetch('get', null, `/gestor/account/${doc}`).then((response) => {
      setFormIsCreatingUser(response.fail)
    })
  }, [formData.get('document')]) // Detecta quando o form esta atualizando um usuario ou regristrando para verificar campos pre existentes no back quando se esta apenas registrando

  const handleInputRequest = (
    key: string,
    inputValue: string,
    type: string | null
  ) => {
    // Inicia o loading para o campo específico
    setInputLoading((prevState) => ({
      ...prevState,
      [key]: true,
    }))

    // Define o endpoint com base no tipo de validação
    const cleanInput =
      type === null ? clearString(inputValue).trim() : inputValue.trim()

    const endpoint =
      type !== null
        ? `/gestor/account/${cleanInput}?type=${type}`
        : `/gestor/account/${cleanInput}`
    // Executa a chamada da API
    api.fetch('get', null, endpoint).then((res) => {
      // Para o loading
      setInputLoading((prevState) => ({
        ...prevState,
        [key]: false, // Atualiza o campo específico para `false`
      }))

      // Atualiza o erro com base na resposta da API
      setInputError((prevState) => ({
        ...prevState,
        [key]: !res.errors ? '' : `${res.errors.message}`,
      }))
    })
  } // Verifica se o campo prenchido ja existe no banco de dados

  useEffect(() => {
    formData = new FormData()

    formData.set('gestorId', user.id)
    setDefaultGestor({ label: user.name, value: user.id })

    setPersonalData([
      ['cpf', 'document', 'document', 'CPF'],
      ['text', 'rg', 'customData', 'RG'],
      [null, 'name', 'name', 'Nome completo'],
      ['date', 'birthDate', 'birthDate', 'Data de aniversário'],
    ])

    setAddressData([
      ['cep', 'postalCode', 'address.postalCode', 'CEP'],
      [null, 'province', 'address.province', 'Bairro'],
      [null, 'address', 'address.address', 'Endereço com rua'],
      [null, 'number', 'address.number', 'Número'],
      [null, 'complement', 'address.complement', 'Complemento'],
    ])
  }, [])

  return (
    <Container>
      <div>
        <Form ref={formRef} onSubmit={handleSubmit}>
          <div>
            <h4 className="pf-title">Dados Pessoais</h4>
            <div className="pf-container">
              {/*
              Caso o campo CPF seja necessário, descomente o código abaixo
              <MaskedInput
                handleChange={handleChange('document')}
                smallPad
                loading={inputLoading.cpf}
                customError={inputError.cpf}
                onBlur={async () => {
                  const { document } = formDataToObject(formData)

                  if (document?.length === 14) {
                    handleInputRequest('cpf', document, null)
                  } else {
                    setInputError((prevState) => ({
                      ...prevState,
                      cpf: '',
                    }))
                  }
                }}
                type="cpf"
                defaultValue={formDataToObject(formData).document}
                placeholder="CPF"
                name="document"
              />
              */}
              {personalData?.map((d: any[], i: number) => {
                const [type, name, key, placeholder] = d
                return (
                  <MaskedInput
                    key={`per_${i}`}
                    handleChange={handleChange(name)}
                    smallPad
                    type={type}
                    placeholder={placeholder}
                    defaultValue={(() => {
                      const value = formData.get(name)

                      return value
                    })()}
                    name={key}
                  />
                )
              })}
              <MaskedInput
                handleChange={handleChange('email')}
                smallPad
                loading={inputLoading.email}
                customError={inputError.email}
                onBlur={async () => {
                  const { email } = formDataToObject(formData)

                  const emailIsvalid = emailRegex.test(email?.trim())

                  if (emailIsvalid && formIsCreatingUser) {
                    handleInputRequest('email', email, 'email')
                  } else {
                    setInputError((prevState) => ({
                      ...prevState,
                      email: '',
                    }))
                  }
                }}
                type=""
                defaultValue={formDataToObject(formData).email}
                placeholder="E-mail"
                name="email"
              />
              <MaskedInput
                handleChange={handleChange('cellphone')}
                smallPad
                loading={inputLoading.cell}
                customError={inputError.cell}
                onBlur={async () => {
                  const { cellphone } = formDataToObject(formData)

                  if (cellphone?.length === 15 && formIsCreatingUser) {
                    handleInputRequest('cell', cellphone, 'cell')
                  } else {
                    setInputError((prevState) => ({
                      ...prevState,
                      cell: '',
                    }))
                  }
                }}
                defaultValue={formDataToObject(formData).cellphone}
                type="cell"
                placeholder="Telefone"
                name="cellphone"
              />
              <Select
                options={selectOptions.civilState}
                name="civilState"
                placeholder={(() => {
                  const v = selectOptions.civilState.find((item) => {
                    const s = formData.get('civilState') || ''
                    return item.value === s.toLowerCase()
                  })
                  return v?.label || 'Estado Civil'
                })()}
                onChange={(e: any) => {
                  setMarried(e.value === 'married')
                  formData.set('civilState', e.value)
                }}
              />
              {married ? (
                <div className="pf-checkbox">
                  <Checkbox
                    label="Incluir dados do cônjuge"
                    setChecked={setIncludesSpouse}
                  />
                </div>
              ) : null}
            </div>

            {includesSpouse ? (
              <>
                <h4 className="pf-title">Dados do cônjuge</h4>
                <div className="pf-container">
                  <MaskedInput
                    smallPad
                    type="cpf"
                    handleChange={(e: any) => handleChange('spouse')(e)}
                    placeholder="CPF"
                    name="document"
                    defaultValue=""
                  />
                  <MaskedInput
                    smallPad
                    type="text"
                    handleChange={(e: any) => handleChange('spouse')(e)}
                    placeholder="Nome completo"
                    name="name"
                    defaultValue=""
                  />
                  <MaskedInput
                    smallPad
                    type="text"
                    handleChange={(e: any) => handleChange('spouse')(e)}
                    placeholder="RG"
                    name="rg"
                    defaultValue=""
                  />
                  <MaskedInput
                    smallPad
                    type="text"
                    handleChange={(e: any) => handleChange('spouse')(e)}
                    placeholder="Nome da mãe"
                    name="motherName"
                    defaultValue=""
                  />
                  <MaskedInput
                    smallPad
                    type="text"
                    handleChange={(e: any) => handleChange('spouse')(e)}
                    placeholder="E-mail"
                    name="email"
                    defaultValue=""
                  />
                  <MaskedInput
                    smallPad
                    type="cellphone"
                    handleChange={(e: any) => handleChange('spouse')(e)}
                    placeholder="Telefone"
                    name="cellphone"
                    defaultValue=""
                  />
                </div>
              </>
            ) : null}

            <h4 className="pf-title">Endereço</h4>
            <div className="pf-container">
              {addressData?.map((d: any[], i: number) => {
                const [type, name, key, placeholder] = d
                return (
                  <MaskedInput
                    key={`addr_${i}`}
                    smallPad
                    type={type}
                    handleChange={(e: any) => handleChange('address')(e)}
                    placeholder={placeholder}
                    defaultValue={(() => {
                      const addr = formData.get('address')
                        ? JSON.parse(formData.get('address'))
                        : {}
                      return addr[name]
                    })()}
                    name={key}
                  />
                )
              })}
              <div className="review">
                <MaskedInput
                  type="text"
                  small
                  disabled
                  name="address.state"
                  placeholder="UF"
                  defaultValue={(() => {
                    const addr = formData.get('address')
                      ? JSON.parse(formData.get('address'))
                      : {}
                    return addr.state
                  })()}
                />
                <MaskedInput
                  type="text"
                  small
                  disabled
                  name="address.city"
                  placeholder="Cidade"
                  defaultValue={(() => {
                    const addr = formData.get('address')
                      ? JSON.parse(formData.get('address'))
                      : {}
                    return addr.city
                  })()}
                />
              </div>
            </div>

            <h4 className="pf-title">Dados bancários</h4>

            <div className="pf-container">
              <MaskedInput
                smallPad
                type="text"
                handleChange={(e: any) => handleChange('pix')(e)}
                placeholder="Banco"
                defaultValue={(() => {
                  const pix = formData.get('pix')
                    ? JSON.parse(formData.get('pix'))
                    : {}
                  return pix.institution
                })()}
                name="institution"
              />
              <Select
                isClearable
                options={selectOptions.pixKeyOptions}
                name="pixType"
                placeholder="Tipo de chave"
                onChange={(e) => {
                  handleChange('pix')({
                    target: { name: 'type', value: e?.value },
                  })
                  setPixType(e?.value)
                  if (e?.value === 'PHONE') {
                    setPixMask('cellWithInternational')
                  }
                  if (e?.value === 'CPF') {
                    setPixMask('cpf')
                  }
                  if (e?.value === 'CNPJ') {
                    setPixMask('cnpj')
                  }
                  if (e?.value === 'EMAIL' || e?.value === 'EVP' || !e?.value) {
                    setPixMask('')
                  }
                }}
                value={selectOptions.pixKeyOptions.find(
                  (option) => option.value === pixType
                )}
              />
              <MaskedInput
                type={pixMask}
                handleChange={(e: any) => {
                  handleChange('pix')({
                    target: { name: 'key', value: e.target.value },
                  })
                }}
                placeholder="Chave"
                defaultValue={(() => {
                  const pix = formData.get('pix')
                    ? JSON.parse(formData.get('pix'))
                    : {}
                  return pix.key
                })()}
                name="key"
              />
            </div>
            <h4 className="pf-title">Especialista</h4>
            <div className="pf-container">
              <Select
                disabled={optionsLevels[user.level] === 'SPECIALIST'}
                value={defaultGestor}
                options={especialistas}
                name="gestorId"
                placeholder="Selecionar especialista"
                onChange={(e) => {
                  formData.set('gestorId', e.value)
                  setDefaultGestor(e)
                }}
              />
            </div>

            <h4 className="pf-title">Produto</h4>
            <div className="review-img-container">
              <Document
                isOnForm
                onClickRemoveFile={(fileName: string) => {
                  setDocumentList((old: any) =>
                    old.filter((ct: any) => {
                      if (!Array.isArray(ct)) {
                        return ct.type !== fileName
                      }
                      return ct[0] !== fileName
                    })
                  )
                }}
                onChange={(_key: any, file: File, name: string) => {
                  setDocumentList((old: any) => [...old, [name, file]])
                }}
                pf
                document={formData.get('document')}
                documentList={documentList}
              />
            </div>
          </div>
        </Form>
        <ButtonNext isAdvance advancePage={() => handleSubmit()} />
      </div>
    </Container>
  )
}
